import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { loadFlowMetrics, loadPipeline } from 'js/actions';
import SourcePipelineContainer from 'js/components/pipelines/SourcePipelineContainer';
import { GET_PIPELINES_REQUEST } from 'js/constants/actionTypes';
import { FLOW_METRICS_INTERVAL_LIFETIME } from 'js/constants/metricsConstants';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { Button } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import { useFlows } from 'new/hooks/adapters/useFlows';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { ModalComponent } from 'new/routes/RouterManager';
import styles from './ModalFlowPreview.module.scss';
import useAppDispatch from 'js/hooks/useAppDispatch';
import ViewExpandedPipelineButton from 'new/elements/ViewExpandedPipelineButton/ViewExpandedPipelineButton';
import {
  ROUTE_ACCESS_INSIGHTS,
  ROUTE_MONITOR,
  ROUTE_PEOPLE_360,
  ROUTE_RESOURCES_360,
} from 'new/constants/pathnames';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';

const buttonTextMap = {
  [`${ROUTE_MONITOR}/resource-triage`]: 'View in Resource Triage',
  [ROUTE_ACCESS_INSIGHTS]: 'View in Access Insights',
  [ROUTE_RESOURCES_360]: 'View in Resources 360',
  [ROUTE_PEOPLE_360]: 'View in People 360',
};

export interface ModalFlowPreviewProps {
  resourceType: string;
  disableFlowSelection?: boolean;
  onItemClick: (type: string, data: any) => void;
  id: string;
}

function Footer(props: ModalFlowPreviewProps & ModalOutProps & { flowNodeId: number }) {
  const location = useLocation();
  const showViewInSectionButton =
    props.resourceType !== RESOURCE_TYPE_FLOW && !props.disableFlowSelection;
  const buttonText = showViewInSectionButton ? buttonTextMap[location.pathname] : undefined;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack />
      <Stack direction="row" alignItems="center">
        {showViewInSectionButton && buttonText ? (
          <Button
            onClick={() =>
              props.onItemClick(RESOURCE_TYPE_FLOW, {
                id: props.flowNodeId,
              })
            }
            variant="outlined"
            color="primary"
          >
            {buttonText}
          </Button>
        ) : null}
        <ViewExpandedPipelineButton
          title="Edit Flow"
          flowNodeId={props.flowNodeId}
          variant="contained"
          className={styles.buttonViewFlow}
        />
      </Stack>
    </Stack>
  );
}

export const ModalFlowPreview: ModalComponent = (props: ModalFlowPreviewProps & ModalOutProps) => {
  const { resourceType, id, setLoading, setFooter } = props;
  const { refreshPipelineById } = usePipelinesAdapter();
  const dispatch = useAppDispatch();
  const { sources, dataSets, sinks } = useSelector((state: any) => state.entities);
  const loading = useIsLoading(GET_PIPELINES_REQUEST);
  const flows = useFlows();

  const flow = flows?.length === 1 ? flows?.[0] : { data: { id: undefined } };

  let flowNodeId;

  switch (resourceType) {
    case RESOURCE_TYPE_SOURCE:
      flowNodeId = sources[id]?.origin_node_id;
      break;

    case RESOURCE_TYPE_DATA_SET:
      flowNodeId = dataSets[id]?.origin_node_id;
      break;

    case RESOURCE_TYPE_SINK:
      flowNodeId = sinks[id]?.origin_node_id;
      break;

    case RESOURCE_TYPE_FLOW:
      flowNodeId = id;
      break;
  }

  useEffect(() => {
    if (flowNodeId) {
      dispatch(loadPipeline(flowNodeId));
    }
  }, []);

  useEffect(() => {
    if (flowNodeId) {
      dispatch(loadFlowMetrics(flowNodeId, { interval: FLOW_METRICS_INTERVAL_LIFETIME }));
    }
  }, [flowNodeId]);

  useEffect(() => {
    setFooter(<Footer {...props} flowNodeId={flowNodeId} />);
  }, []);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  if (!flows || !flows.length || flows.length > 1) {
    return null;
  }

  const onRefreshPipeline = () => {
    if (flowNodeId) {
      refreshPipelineById(Number(flowNodeId));
      // Mirror the mount fetch (fixed Lifetime). The previous
      // `requestFlowMetrics(flowNodeId)` passed no sourceId/interval, so the
      // adapter short-circuited and refresh fetched no metrics at all.
      dispatch(
        loadFlowMetrics(Number(flowNodeId), {
          interval: FLOW_METRICS_INTERVAL_LIFETIME,
        }),
      );
    }
  };

  return (
    <div>
      <SourcePipelineContainer
        isPreview
        // Quick structural preview: the interval toggle is hidden here. Metrics
        // are Lifetime-only (see mount + refresh above); a visible toggle that
        // did nothing was misleading. The full time control lives on flow detail.
        hideMetricsSelector
        loading={loading}
        dataSource={flows[0].data}
        onItemClick={props.onItemClick}
        scaleFactor={1}
        refreshPipeline={onRefreshPipeline}
      />
    </div>
  );
};

ModalFlowPreview.modalConfig = {
  name: 'ModalFlowPreview',
  title: 'Flow preview',
  fullWidth: true,
};
